import type { RunDescriptor } from '../contracts/game'
import type { RunFactory, RunInstance } from '../game/RunFactory'

const MAX_STEERING_Q = 32_767

interface RivalDefinition {
  readonly id: string
  readonly name: string
  readonly lookAhead: number
  readonly aggression: number
  readonly steeringNoise: number
}

export interface RivalSnapshot {
  readonly id: string
  readonly name: string
  readonly alive: boolean
  readonly distance: number
  readonly position: { readonly x: number; readonly y: number; readonly z: number }
}

const DEFINITIONS: readonly RivalDefinition[] = [
  { id: 'axiom', name: 'AXIOM // 7', lookAhead: 34, aggression: 0.72, steeringNoise: 0.04 },
  { id: 'kestrel', name: 'KESTREL // 3', lookAhead: 42, aggression: 0.9, steeringNoise: 0.08 },
  { id: 'rift', name: 'RIFT // 9', lookAhead: 50, aggression: 1.08, steeringNoise: 0.13 },
]

interface RivalRuntime {
  readonly definition: RivalDefinition
  readonly run: RunInstance
  steeringQ: number
}

function deterministicNoise(seed: number, tick: number, index: number): number {
  let value = (seed ^ Math.imul(tick + 1, 0x9e3779b1) ^ Math.imul(index + 7, 0x85ebca6b)) >>> 0
  value ^= value >>> 16
  value = Math.imul(value, 0x7feb352d)
  value ^= value >>> 15
  return (value >>> 0) / 0xffff_ffff * 2 - 1
}

export class RivalController {
  private readonly factory: RunFactory
  private descriptor: RunDescriptor | null = null
  private runtimes: RivalRuntime[] = []

  public constructor(factory: RunFactory) {
    this.factory = factory
  }

  public start(descriptor: RunDescriptor): void {
    this.descriptor = { ...descriptor }
    this.runtimes = DEFINITIONS.map((definition) => ({
      definition,
      run: this.factory.create(descriptor),
      steeringQ: 0,
    }))
  }

  public step(): void {
    if (this.descriptor === null) return
    for (let index = 0; index < this.runtimes.length; index += 1) {
      const runtime = this.runtimes[index]!
      const state = runtime.run.simulation.state
      if (!state.player.alive) continue
      if (state.tick % 6 === 0) {
        const targetZ = state.player.distance + runtime.definition.lookAhead
        const chunk = runtime.run.world.activeChunks.find((entry) => targetZ >= entry.startZ && targetZ < entry.startZ + entry.length)
        const corridors = chunk?.safeCorridors ?? [{ x: 0, halfWidth: 4 }]
        const trackCenter = runtime.run.world.trackCenterX(targetZ)
        let target = trackCenter + (corridors[0]?.x ?? 0)
        for (const corridor of corridors) {
          const candidate = trackCenter + corridor.x
          if (Math.abs(candidate - state.player.position.x) < Math.abs(target - state.player.position.x)) target = candidate
        }
        const error = target - state.player.position.x - state.player.lateralVelocity * 0.14
        const noise = deterministicNoise(this.descriptor.seed, state.tick / 6, index) * runtime.definition.steeringNoise
        const steering = Math.max(-1, Math.min(1, error * 0.24 * runtime.definition.aggression + noise))
        runtime.steeringQ = Math.round(steering * MAX_STEERING_Q)
      }
      runtime.run.simulation.step({ tick: state.tick, steeringQ: runtime.steeringQ })
    }
  }

  public snapshots(): readonly RivalSnapshot[] {
    return this.runtimes.map(({ definition, run }) => ({
      id: definition.id,
      name: definition.name,
      alive: run.simulation.state.player.alive,
      distance: run.simulation.state.player.distance,
      position: { ...run.simulation.state.player.position },
    }))
  }
}
